import { useEffect, useState } from 'react'
import { SURVEY_QUESTION_TYPE } from '../../domain/domainCodes'
import { ListChecks, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import DatePicker from '../../components/DatePicker'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import IconButton from '../../components/IconButton'
import SegmentedControl from '../../components/SegmentedControl'
import Select from '../../components/Select'
import { EmptyState } from '../../components/States'
import TextField from '../../components/TextField'
import { SURVEY_TYPE, findSurveyFor, getSurvey, saveSurvey } from '../engagement/engagementService'
import { strings as S } from './strings'

const D = S.detail.surveyPanel

/** The legacy's three, in its order — `surveyStp1..3` in Components/SurveyModal. */
const STEPS = ['info', 'properties', 'questions']

/** `SurveyQuestionType`: Radio 1, CheckBox 2, YesNo 3, Stars 4, ShortText 5, LongText 6.
    Only the first two are answered from a list the author writes. */
const CHOICE_TYPES = [SURVEY_QUESTION_TYPE.RADIO, SURVEY_QUESTION_TYPE.CHECKBOX]

const EMPTY = {
  id: 0,
  title: '',
  titleEn: '',
  descriptionAr: '',
  descriptionEn: '',
  enabled: false,
  mandatory: false,
  timeRestricted: false,
  startDate: '',
  endDate: '',
  restriction: null,
  groupIds: [],
  questions: [],
}

const EMPTY_QUESTION = { textAr: '', textEn: '', type: SURVEY_QUESTION_TYPE.RADIO, answers: [] }

/**
 * استبيان البرنامج — the legacy's survey modal, opened from the programme's own page.
 *
 * The card used to navigate to the surveys screen, which is not what the legacy does and
 * loses the programme on the way. `showSurveyModalForType('program', id)` asks
 * `CheckSurveyForType` whether this programme already has a survey and opens the SAME modal
 * in place — for create or for edit — with the type and the target already decided. So
 * neither is a field here: it is this programme's survey, which is why the drawer opened.
 *
 * The three steps are the legacy's own: what the survey IS, how it behaves, and its
 * questions. `SaveSurveyCommand` carries the questions and their answers along with the
 * survey, so all three leave in one call.
 */
export default function ProgramSurveyDrawer({ open, program, groups, onClose, onSaved }) {
  const [values, setValues] = useState(EMPTY)
  const [step, setStep] = useState(0)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(EMPTY_QUESTION)

  useEffect(() => {
    if (!open || !program?.id) return undefined

    let cancelled = false
    setStep(0)
    setErrors({})
    setDraft(EMPTY_QUESTION)
    setLoading(true)

    /* Create or edit, decided the way the legacy decides it. */
    findSurveyFor('program', program.id)
      .then(async (surveyId) => {
        const existing = surveyId > 0 ? await getSurvey(surveyId).catch(() => null) : null
        if (cancelled) return
        setValues(existing ? { ...EMPTY, ...existing, id: surveyId } : { ...EMPTY })
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setValues({ ...EMPTY })
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, program])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const setDraftField = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }))

  const addQuestion = () => {
    if (!draft.textAr.trim() || !draft.textEn.trim()) return
    setValues((previous) => ({
      ...previous,
      /* Negative until saved — the convention the rest of this feature uses for a row the
         server has not seen yet. */
      questions: [
        ...previous.questions,
        { ...draft, id: -(previous.questions.filter((q) => q.id < 0).length + 1) },
      ],
    }))
    setDraft(EMPTY_QUESTION)
  }

  const answersOf = (question) =>
    CHOICE_TYPES.includes(Number(question.type)) ? (question.answers ?? []) : []

  const validateStep = () => {
    const next = {}
    if (step === 0) {
      if (!values.title.trim()) next.title = S.form.required
      if (!values.titleEn.trim()) next.titleEn = S.form.required
    }
    if (step === 1 && values.timeRestricted) {
      if (!values.startDate) next.startDate = S.form.required
      if (!values.endDate) next.endDate = S.form.required
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!validateStep()) return

    /* The steps run in order, as the legacy's stepper does. */
    if (step < STEPS.length - 1) {
      setStep(step + 1)
      return
    }

    setSaving(true)
    const outcome = await saveSurvey({ ...values, type: SURVEY_TYPE.program, programId: program.id })
    setSaving(false)
    onSaved(outcome)
  }

  const typeOptions = Object.entries(D.questionTypes).map(([value, label]) => ({ value, label }))

  return (
    <FormDrawer
      open={open}
      size="lg"
      formId="program-survey"
      icon={ListChecks}
      title={D.title}
      subtitle={program?.name}
      submitLabel={step < STEPS.length - 1 ? D.next : S.form.save}
      cancelLabel={S.close}
      submitting={saving || loading}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormBody>
        <SegmentedControl
          items={STEPS.map((id, index) => ({ id, label: `${index + 1}. ${D.steps[id]}` }))}
          value={STEPS[step]}
          onChange={(id) => setStep(STEPS.indexOf(id))}
          ariaLabel={D.title}
        />

        {loading ? (
          <p className="pd__survey-note">{D.loading}</p>
        ) : (
          <>
            {step === 0 && (
              <>
              <FormCols>
                <FormRow label={D.nameAr} error={errors.title}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values.title}
                      onChange={set('title')}
                      invalid={!!errors.title}
                    />
                  )}
                </FormRow>
                <FormRow label={D.nameEn} error={errors.titleEn}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.titleEn}
                      onChange={set('titleEn')}
                      invalid={!!errors.titleEn}
                    />
                  )}
                </FormRow>
                <FormRow label={D.descriptionAr}>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={3}
                      value={values.descriptionAr}
                      onChange={set('descriptionAr')}
                    />
                  )}
                </FormRow>
                <FormRow label={D.descriptionEn}>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={3}
                      dir="ltr"
                      value={values.descriptionEn}
                      onChange={set('descriptionEn')}
                    />
                  )}
                </FormRow>
              </FormCols>
              {/* ENABLED LIVES ON STEP ONE, where the legacy keeps it — `chkBxActiveSurvey`
                  is inside `#surveyInfo`, not `#surveyProperties`. It is part of what the
                  survey IS, not of how it behaves. */}
              <SwitchField
                checked={values.enabled}
                onChange={set('enabled')}
                label={D.enabled}
                hint={D.enabledHint}
              />
              </>
            )}

            {step === 1 && (
              <>
                {/* Neither the type nor the target is asked for — that is the whole point of
                    opening this from the programme rather than from the surveys screen. */}
                <InfoNote>{D.targetNote}</InfoNote>

                <SwitchField
                  checked={values.mandatory}
                  onChange={set('mandatory')}
                  label={D.mandatory}
                />
                <SwitchField
                  checked={values.timeRestricted}
                  onChange={set('timeRestricted')}
                  label={D.timeRestricted}
                />

                {values.timeRestricted && (
                  <FormCols>
                    <FormRow label={D.startDate} error={errors.startDate}>
                      {(id) => (
                        <DatePicker id={id} value={values.startDate} onChange={set('startDate')} />
                      )}
                    </FormRow>
                    <FormRow label={D.endDate} error={errors.endDate}>
                      {(id) => (
                        <DatePicker id={id} value={values.endDate} onChange={set('endDate')} />
                      )}
                    </FormRow>
                  </FormCols>
                )}

                <GroupPicker
                  options={groups}
                  selected={(values.groupIds ?? []).map(String)}
                  onChange={(next) => set('groupIds')(next.map(Number))}
                  legend={D.groups}
                  selectAllLabel={D.selectAll}
                  filterLabel={D.filter}
                />
              </>
            )}

            {step === 2 && (
              <>
                {/* The legacy's third step is an "add question" button over a table. The
                    answer list only exists for the two types that are answered from one — a
                    Yes/No or a star rating writes its own. */}
                <div className="pd__q-adder">
                  <FormCols>
                    <FormRow label={D.questionAr}>
                      {(id) => (
                        <TextField
                          id={id}
                          value={draft.textAr}
                          onChange={setDraftField('textAr')}
                        />
                      )}
                    </FormRow>
                    <FormRow label={D.questionEn}>
                      {(id) => (
                        <TextField
                          id={id}
                          dir="ltr"
                          value={draft.textEn}
                          onChange={setDraftField('textEn')}
                        />
                      )}
                    </FormRow>
                    <FormRow label={D.questionType}>
                      {(id) => (
                        <Select
                          id={id}
                          value={String(draft.type)}
                          onChange={(value) => setDraftField('type')(Number(value))}
                          options={typeOptions}
                        />
                      )}
                    </FormRow>
                    {CHOICE_TYPES.includes(Number(draft.type)) && (
                      <FormRow label={D.answers} hint={D.answersHint}>
                        {(id) => (
                          <TextField
                            id={id}
                            value={(draft.answers ?? []).map((answer) => answer.textAr).join(' | ')}
                            onChange={(value) =>
                              setDraftField('answers')(
                                value
                                  .split('|')
                                  .map((text) => text.trim())
                                  .filter(Boolean)
                                  .map((text, index) => ({
                                    id: -(index + 1),
                                    textAr: text,
                                    textEn: text,
                                  })),
                              )
                            }
                          />
                        )}
                      </FormRow>
                    )}
                  </FormCols>

                  <Button
                    type="button"
                    onClick={addQuestion}
                    disabled={!draft.textAr.trim() || !draft.textEn.trim()}
                  >
                    <Plus size={16} strokeWidth={2} />
                    {D.addQuestion}
                  </Button>
                </div>

                {values.questions.length === 0 ? (
                  <EmptyState title={D.noQuestions} />
                ) : (
                  <ol className="pd__q-list">
                    {values.questions.map((question, index) => (
                      <li key={question.id ?? index} className="pd__q-row">
                        <span className="pd__q-index tabular">{index + 1}</span>
                        <span className="pd__q-texts">
                          <bdi>{question.textAr}</bdi>
                          <bdi className="pd__q-en" dir="ltr">
                            {question.textEn}
                          </bdi>
                          <span className="pd__q-meta">
                            <bdi>{D.questionTypes[String(question.type)]}</bdi>
                            {answersOf(question).length > 0 && (
                              <bdi>
                                {' · '}
                                {answersOf(question)
                                  .map((answer) => answer.textAr)
                                  .join('، ')}
                              </bdi>
                            )}
                          </span>
                        </span>
                        <IconButton
                          icon={Trash2}
                          label={D.removeQuestion}
                          tone="danger"
                          onClick={() =>
                            setValues((previous) => ({
                              ...previous,
                              questions: previous.questions.filter((item) => item !== question),
                            }))
                          }
                        />
                      </li>
                    ))}
                  </ol>
                )}
              </>
            )}
          </>
        )}
      </FormBody>
    </FormDrawer>
  )
}
